import React, { useState, useEffect } from 'react';
import type { Cidade } from '../types';
import { criarGestorMunicipal } from '../types';

export interface GestorMunicipalStepProps {
  cidade: Cidade;
  onGestorCriado: (cidade: Cidade) => void;  // retorna cidade atualizada com temGestor=true
  onNext: () => void;                          // skip se gestor já existe
}

// Brasão/Selo de Ouro SVG Monumental para o Prefeito (Com brilho de joia reluzente e pulsação nobre)
const GoldSealSvg = () => (
  <div style={{ 
    animation: 'corePulse 3.5s ease-in-out infinite',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    position: 'relative'
  }}>
    <svg width="115" height="115" viewBox="0 0 24 24" fill="none" style={{ opacity: 0.98 }}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill="url(#gold-grad)" stroke="#fbbf24" strokeWidth="1.5" />
      <path d="M12 4.5 L13.8 9.1 L18.7 9.3 L14.9 12.4 L16.1 17.2 L12 14.5 L7.9 17.2 L9.2 12.4 L5.3 9.3 L10.2 9.1 Z" fill="#0f0b29" />
      
      {/* Brilho de Diamante/Ouro Fino e Côncavo reluzindo na ponta superior direita do Brasão */}
      <path d="M19 2 Q19 5 22 5 Q19 5 19 8 Q19 5 16 5 Q19 5 19 2 Z" fill="#ffffff" style={{
        animation: 'sparkle 2.8s ease-in-out infinite',
        transformOrigin: '19px 5px',
        filter: 'drop-shadow(0 0 4px #ffe066)'
      }} />

      <defs>
        <linearGradient id="gold-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffe066" />
          <stop offset="50%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#d97706" />
        </linearGradient>
      </defs>
    </svg>
  </div>
);

export function GestorMunicipalStep({ cidade, onGestorCriado, onNext }: GestorMunicipalStepProps) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha] = useState('Demo@2026'); // Senha mantida internamente como Demo@2026 para a credencial padrão
  const [saving, setSaving] = useState(false);
  const [resultado, setResultado] = useState<{ email: string; senhaPadrao: string; criado: boolean } | null>(null);
  const [erro, setErro] = useState('');

  // Estados locais para interações de luxo nos inputs e botão
  const [focusedField, setFocusedField] = useState<'nome' | 'email' | null>(null);
  const [buttonHovered, setButtonHovered] = useState(false);

  // Se a cidade já tem gestor, pula automaticamente (envelopado em useEffect para evitar warning de render phase)
  useEffect(() => {
    if (cidade.temGestor) {
      onNext();
    }
  }, [cidade.temGestor, onNext]);

  if (cidade.temGestor) {
    return null;
  }

  const handleCriar = async () => {
    setErro('');
    if (nome.trim().length <= 3) {
      setErro('Informe seu nome completo.');
      return;
    }
    setSaving(true);
    try {
      const res = await criarGestorMunicipal(cidade.id, { 
        nome: nome.trim(), 
        email: email.trim() || undefined,
        senha: senha.trim() || undefined
      });
      localStorage.setItem('sim_gestor_nome', nome.trim());
      setResultado(res);
      // Marca cidade como com gestor
      onGestorCriado({ ...cidade, temGestor: true });
    } catch (e: any) {
      setErro(e.message ?? 'Erro ao criar gestor.');
    }
    setSaving(false);
  };

  return (
    <div style={{ 
      display:'flex', 
      flexDirection:'column', 
      gap:40, // Aumentado significativamente o gap para dar muito respiro
      margin: '-40px -40px', // Anula perfeitamente o padding p-10 (40px) do container Tailwind do wizard
      padding: '52px 48px', // Espaçamento luxuoso e imponente nas laterais, topo e base
      background: 'rgba(15,10,35,0.96)',
      borderRadius: '24px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(135deg, rgba(18,12,38,0.97), rgba(12,8,26,0.99), rgba(6,4,15,0.99), rgba(18,12,38,0.97)), linear-gradient(135deg, #ffe066 0%, #fbbf24 25%, #d97706 50%, #7c2d12 75%, #fbbf24 100%)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      backgroundSize: '200% 200%',
      animation: 'goldenBorderBackground 20s ease-in-out infinite', // Velocidade de 20s para movimentação sutil e lenta
      boxShadow: '0 30px 70px rgba(0,0,0,0.65), 0 0 45px rgba(245,158,11,0.15)',
      position: 'relative',
      overflow: 'hidden' // Impede que o feixe de luz saia do card
    }}>
      <style>{`
        @keyframes goldenBorderBackground {
          0% {
            background-position: 0% 0%;
          }
          50% {
            background-position: 100% 100%;
          }
          100% {
            background-position: 0% 0%;
          }
        }
        @keyframes corePulse {
          0%, 100% {
            transform: scale(1);
            filter: drop-shadow(0 6px 14px rgba(245,158,11,0.4)) drop-shadow(0 0 10px rgba(251,191,36,0.15));
          }
          50% {
            transform: scale(1.05);
            filter: drop-shadow(0 12px 28px rgba(245,158,11,0.85)) drop-shadow(0 0 25px rgba(251,191,36,0.4));
          }
        }
        @keyframes sparkle {
          0%, 100% {
            transform: scale(0.3) rotate(0deg);
            opacity: 0.15;
          }
          50% {
            transform: scale(1.2) rotate(90deg);
            opacity: 1;
          }
        }
        @keyframes goldSweep {
          0% {
            transform: translateX(-150%) skewX(-30deg);
          }
          40%, 100% {
            transform: translateX(200%) skewX(-30deg);
          }
        }
        @keyframes pulse3d {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 4px 18px rgba(16, 185, 129, 0.4), inset 0 1px 0 rgba(255,255,255,0.45);
          }
          50% {
            transform: scale(1.02);
            box-shadow: 0 10px 32px rgba(16, 185, 129, 0.75), inset 0 1px 0 rgba(255,255,255,0.6);
          }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Feixe de Luz de Ouro Líquido que corre diagonalmente pelo card periodicamente (Shimmer de Ouro) */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: '24px',
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 1
      }}>
        <div style={{
          width: '60%',
          height: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(255, 224, 102, 0.12) 50%, transparent)',
          animation: 'goldSweep 6.5s ease-in-out infinite',
        }} />
      </div>

      {/* Header Dourado Monumental do Prefeito */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 28, zIndex: 2, position: 'relative' }}>
        <GoldSealSvg />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize:11,fontWeight:900,color:'#fbbf24',letterSpacing:'0.2em',textTransform:'uppercase',marginBottom:6, textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
            CREDENCIAL DE HOMOLOGAÇÃO E SIMULAÇÃO
          </div>
          <h1 style={{ 
            fontSize:14, // Reduzido de 19 para 14 para acomodar o texto longo em caixa alta com elegância
            fontWeight:500, // Fonte mais fina para elegância contrastante
            color:'rgba(255,255,255,0.85)',
            letterSpacing:'0.04em',
            textTransform:'uppercase',
            lineHeight:1.3,
            marginTop: 0, // Removido o espaçamento vertical entre a credencial e o título principal
            marginBottom: 0
          }}>
            GABINETE DE ALTA AUTORIDADE DA PREFEITURA DE
            <div style={{
              display: 'inline-block',
              fontSize: 36, // Aumentado em 18% para maior imponência e legibilidade
              fontWeight: 900,
              marginTop: 15,
              padding: '10px 26px', // Proporção expandida
              borderRadius: '14px', // border-radius proporcional
              border: '1.5px solid rgba(251, 191, 36, 0.45)', // Borda dourada sutil
              background: 'rgba(255, 255, 255, 0.03)', // Fundo escuro translúcido
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              backgroundImage: 'linear-gradient(135deg, #ffe066 0%, #fbbf24 60%, #f59e0b 100%)', // Texto com gradiente dourado
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 2px 8px rgba(251, 191, 36, 0.4))', // Brilho de ouro reluzente
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              lineHeight: 1.1,
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37), inset 0 1px 0 0 rgba(255, 255, 255, 0.05)'
            }}>
              {cidade.nome} / {cidade.uf}
            </div>
          </h1>
        </div>
      </div>

      {/* Divisor com Rótulo de Prestígio e Linha Dourada em Gradiente */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: -15, marginBottom: 12, zIndex: 2, position: 'relative' }}>
        <div style={{ 
          height: '2.5px', 
          width: '100%', 
          background: 'linear-gradient(to right, #ffe066 0%, #fbbf24 25%, rgba(245, 158, 11, 0.2) 75%, transparent 100%)',
          borderRadius: 2
        }} />
        <div style={{
          fontSize: 10,
          fontWeight: 800,
          color: '#fbbf24',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          opacity: 0.8,
          display: 'flex',
          alignItems: 'center',
          gap: 6
        }}>
          <span>✦</span> CREDENCIAMENTO EXCLUSIVO DE CHEFE DO EXECUTIVO MUNICIPAL • SEGURANÇA SOBERANA <span>✦</span>
        </div>
      </div>

      {resultado ? (
        // Sucesso
        <div style={{ padding:'24px',borderRadius:16,background:'rgba(34,197,94,0.08)',border:'1px solid rgba(34,197,94,0.35)', animation: 'fadeUp 0.4s ease', display:'flex', flexDirection:'column', gap:16, zIndex: 2, position: 'relative' }}>
          <div style={{ fontSize:24 }}>✅</div>
          <div style={{ fontSize:17,fontWeight:700,color:'#86efac' }}>
            {resultado.criado ? 'Gestor criado com sucesso!' : 'Gestor já existia — credencial pronta.'}
          </div>
          <div style={{ display:'flex',flexDirection:'column',gap:6,padding:'16px',background:'rgba(0,0,0,0.25)',borderRadius:12,border:'1px solid rgba(255,255,255,0.05)' }}>
            <div style={{ fontSize:14,color:'#94a3b8' }}>📧 Usuário/Email: <strong style={{ color:'#f1f5f9' }}>{resultado.email}</strong></div>
            <div style={{ fontSize:14,color:'#94a3b8' }}>🔑 Senha de acesso: <strong style={{ color:'#fbbf24' }}>{resultado.senhaPadrao}</strong></div>
          </div>
          <button onClick={onNext} style={{ width:'100%',padding:'15px',borderRadius:12,border:'none',cursor:'pointer',background:'linear-gradient(135deg,#10b981,#059669)',color:'#fff',fontSize:15,fontWeight:800,textTransform:'uppercase',letterSpacing:'0.03em',boxShadow:'0 4px 15px rgba(16,185,129,0.3)' }}>
            Selecionar Escolas →
          </button>
        </div>
      ) : (
        // Form
        <div style={{ display:'flex',flexDirection:'column',gap:28, zIndex: 2, position: 'relative' }}>
          {erro && (
            <div style={{ padding:'12px 16px',borderRadius:10,background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.2)',fontSize:13,color:'#f87171' }}>
              {erro}
            </div>
          )}

          {/* Campo Nome */}
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <label style={{ fontSize:13, fontWeight:800, color:'#fbbf24', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Nome do Prefeito:</label>
            <input value={nome} onChange={e => setNome(e.target.value)}
              placeholder="Digite o nome completo do Prefeito..."
              style={{ 
                padding:'15px 18px',
                borderRadius:12,
                border: focusedField === 'nome' ? '2.5px solid #d97706' : '2.5px solid #fbbf24', // Aprofunda o dourado no foco
                background:'#ffffff',
                color:'#0b0a21',
                fontSize:15,
                fontWeight:700,
                outline:'none',
                boxShadow: focusedField === 'nome' 
                  ? '0 0 14px rgba(251, 191, 36, 0.55), inset 0 2px 4px rgba(0,0,0,0.06)' 
                  : 'inset 0 2px 4px rgba(0,0,0,0.08)',
                transition: 'all 0.22s ease-in-out',
                width: '100%',
                boxSizing: 'border-box'
              }}
              onFocus={() => setFocusedField('nome')}
              onBlur={() => setFocusedField(null)}
            />
          </div>

          {/* Linha com E-mail à esquerda e Botão à direita */}
          <div style={{ display:'flex', gap:20, alignItems:'flex-start', width:'100%' }}>
            
            {/* Coluna E-mail */}
            <div style={{ flex:1, display:'flex', flexDirection:'column', gap:10 }}>
              <label style={{ fontSize:13, fontWeight:800, color:'#fbbf24', textTransform: 'uppercase', letterSpacing: '0.06em' }}>E-mail de acesso:</label>
              <input value={email} onChange={e => setEmail(e.target.value)}
                placeholder="Ex: prefeito@secretaria.edu (opcional)"
                type="email"
                style={{ 
                  padding:'15px 18px',
                  borderRadius:12,
                  border: focusedField === 'email' ? '2.5px solid #d97706' : '2.5px solid #fbbf24', // Aprofunda o dourado no foco
                  background:'#ffffff',
                  color:'#0b0a21',
                  fontSize:15,
                  fontWeight:700,
                  outline:'none',
                  boxShadow: focusedField === 'email' 
                    ? '0 0 14px rgba(251, 191, 36, 0.55), inset 0 2px 4px rgba(0,0,0,0.06)' 
                    : 'inset 0 2px 4px rgba(0,0,0,0.08)',
                  transition: 'all 0.22s ease-in-out',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
              />
            </div>

            {/* Botão ao lado direito do e-mail */}
            <button onClick={handleCriar} disabled={saving}
              style={{ 
                marginTop:32, // Alinha perfeitamente com o input (Label + Gap)
                padding:'16px 28px',
                borderRadius:'12px',
                border:'none',
                borderTop:'1.5px solid rgba(255,255,255,0.45)',
                borderBottom: buttonHovered ? '8px solid #047857' : '6px solid #047857', // Relevo 3D aprofundado no hover
                cursor: saving ? 'not-allowed' : 'pointer',
                background: buttonHovered 
                  ? 'linear-gradient(135deg, #34d399, #059669)' // Verde neon ativo no hover
                  : 'linear-gradient(135deg, #10b981, #059669)',
                color:'#fff',
                fontSize:'15px',
                fontWeight:900,
                textShadow:'0 1px 2px rgba(0,0,0,0.35)',
                letterSpacing:'0.04em',
                textTransform:'uppercase',
                display:'flex',
                alignItems:'center',
                justifyContent:'center',
                gap:8,
                transition:'all 0.18s cubic-bezier(0.2, 0.8, 0.2, 1)',
                transform: buttonHovered ? 'translateY(-3px)' : 'translateY(0)', // Flutuação suave para cima no hover
                animation: saving ? 'none' : 'pulse3d 2.6s ease-in-out infinite',
                flexShrink: 0,
                boxShadow: buttonHovered 
                  ? '0 14px 28px rgba(16, 185, 129, 0.45), 0 0 35px rgba(16, 185, 129, 0.25)' 
                  : '0 4px 18px rgba(16, 185, 129, 0.4)',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={() => setButtonHovered(true)}
              onMouseLeave={() => setButtonHovered(false)}
              onMouseDown={e => {
                if (saving) return;
                e.currentTarget.style.transform = 'translateY(3px)';
                e.currentTarget.style.borderBottomWidth = '2px';
              }}
              onMouseUp={e => {
                if (saving) return;
                e.currentTarget.style.transform = buttonHovered ? 'translateY(-3px)' : '';
                e.currentTarget.style.borderBottomWidth = buttonHovered ? '8px' : '6px';
              }}
            >
              {/* Shimmer rápido de luz passando no hover */}
              {buttonHovered && !saving && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.45) 50%, transparent)',
                  animation: 'goldSweep 1.6s ease-in-out infinite', // Reutiliza goldSweep de forma muito mais veloz
                  pointerEvents: 'none',
                  zIndex: 1
                }} />
              )}

              <span style={{ zIndex: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                {saving ? (
                  <>
                    <span style={{ width:12,height:12,borderRadius:'50%',border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'#fff',display:'inline-block',animation:'spin 0.75s linear infinite' }} />
                    Criando...
                  </>
                ) : (
                  <>✓ Criar Gestor</>
                )}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
